import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { PixelIcon } from "./PixelIcon";

interface VoiceRecognition {
  lang: string; interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void; stop: () => void;
}
type VoiceWindow = Window & { SpeechRecognition?: new () => VoiceRecognition; webkitSpeechRecognition?: new () => VoiceRecognition };

export function SearchInput({ value, onChange, onSubmit, autoFocus = false }: {
  value: string; onChange: (value: string) => void; onSubmit: () => void; autoFocus?: boolean;
}) {
  const recognition = useRef<VoiceRecognition | null>(null);
  const [listening, setListening] = useState(false);
  const [notice, setNotice] = useState("");
  useEffect(() => () => { recognition.current?.stop(); }, []);
  const dictate = () => {
    if (listening) { recognition.current?.stop(); return; }
    const voiceWindow = window as VoiceWindow;
    const Voice = voiceWindow.SpeechRecognition ?? voiceWindow.webkitSpeechRecognition;
    if (!Voice) { setNotice("Voice input isn’t supported here. Please type your search."); return; }
    const voice = new Voice(); recognition.current = voice;
    voice.lang = navigator.language || "en-US"; voice.interimResults = true;
    voice.onresult = (event) => onChange(Array.from(event.results).map((result) => result[0]?.transcript ?? "").join(" "));
    voice.onerror = (event) => { setListening(false); setNotice(event.error === "not-allowed" ? "Allow microphone access to use voice search." : "Voice input stopped. Please try again or type."); };
    voice.onend = () => setListening(false);
    setNotice("");
    try { voice.start(); setListening(true); } catch { setNotice("Voice input couldn’t start. Please type your search."); }
  };
  return <div>
    <form role="search" className="flex items-center gap-1" onSubmit={(event) => { event.preventDefault(); recognition.current?.stop(); if (value.trim()) onSubmit(); }}>
      <input autoFocus={autoFocus} aria-label="Search the website" placeholder={listening ? "Listening…" : "What are you looking for?"} value={value} onChange={(event) => onChange(event.target.value)} maxLength={500} className="h-11 min-w-0 flex-1 bg-transparent px-2 text-base outline-none placeholder:text-muted-foreground" />
      <Button type="button" variant="ghost" size="icon" aria-label={listening ? "Stop dictation" : "Dictate search"} title={listening ? "Stop dictation" : "Dictate search"} onClick={dictate} className={listening ? "dex-mic-active text-primary" : "dex-pixel-control"}><PixelIcon kind={listening ? "stop" : "mic"} /></Button>
      <Button type="submit" variant="ghost" size="icon" disabled={!value.trim()} aria-label="Run search" title="Run search" className="dex-pixel-control"><PixelIcon kind="arrow" /></Button>
    </form>
    {(notice || listening) && <p role="status" className="px-2 pt-2 text-xs text-muted-foreground">{notice || "Listening…"}</p>}
  </div>;
}